import { createFileRoute } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { getSuppliers, createSupplier, updateSupplier, deleteSupplier } from '@/services/firestore'
import { friendlyError } from '@/utils/friendlyError'
import { Search, Plus, Truck, Edit3, Trash2, X, Save } from 'lucide-react'

export const Route = createFileRoute('/admin/settings')({
  component: SuppliersPage,
})

interface Supplier { id: string; name: string; phone?: string; email?: string; ntn?: string; address?: string; city?: string; contactPerson?: string; balance?: number;[k: string]: any }

function SuppliersPage() {
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<Supplier | null>(null)
  const [form, setForm] = useState({ name: '', phone: '', email: '', ntn: '', address: '', city: '', contactPerson: '' })

  useEffect(() => { load() }, [])
  const load = async () => {
    try { setSuppliers(await getSuppliers() as Supplier[]) } catch (err) { console.error(err) }
    finally { setLoading(false) }
  }

  const filtered = suppliers.filter(s => !search || s.name.toLowerCase().includes(search.toLowerCase()) || s.phone?.includes(search) || s.ntn?.includes(search))

  const openNew = () => { setEditing(null); setForm({ name: '', phone: '', email: '', ntn: '', address: '', city: '', contactPerson: '' }); setShowForm(true) }
  const openEdit = (s: Supplier) => { setEditing(s); setForm({ name: s.name, phone: s.phone || '', email: s.email || '', ntn: s.ntn || '', address: s.address || '', city: s.city || '', contactPerson: s.contactPerson || '' }); setShowForm(true) }
  const handleSave = async () => {
    try { if (editing) { await updateSupplier(editing.id, form as any) } else { await createSupplier(form as any) }; setShowForm(false); load() }
    catch (err: any) { alert(friendlyError(err, 'Could not save supplier')) }
  }
  const handleDelete = async (id: string) => { if (!confirm('Delete?')) return; try { await deleteSupplier(id); load() } catch (err: any) { alert(friendlyError(err, 'Could not delete supplier')) } }

  const f = (n: number) => n.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const totalPayable = suppliers.reduce((s, sup) => s + (sup.balance || 0), 0)

  if (loading) return (
    <div className="p-4 pt-14 lg:pt-5 lg:p-5 bg-[#f0f5ff] min-h-screen animate-pulse">
      <div className="flex items-center justify-between mb-4"><div className="h-5 w-32 bg-slate-200 rounded-lg" /><div className="h-8 w-28 bg-slate-200 rounded-lg" /></div>
      <div className="bg-white rounded-xl h-12 border border-slate-200/60 mb-3" />
      <div className="bg-white rounded-xl h-72 border border-slate-200/60" />
    </div>
  )

  return (
    <div className="p-4 pt-14 lg:pt-5 lg:p-5 bg-[#f0f5ff] min-h-screen">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2"><Truck className="w-5 h-5 text-slate-600" />Suppliers</h1>
          <p className="text-xs text-slate-500 mt-0.5">{suppliers.length} registered vendors</p>
        </div>
        <button onClick={openNew} className="flex items-center gap-1.5 bg-blue-600 text-white px-4 py-2 rounded-xl text-[11px] font-semibold hover:bg-blue-700 hover:shadow-lg transition-all duration-200">
          <Plus className="w-3.5 h-3.5" />Add Supplier
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/60 p-3 mb-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name, phone, or NTN..."
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 bg-slate-50/50 transition-all" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200/60 overflow-hidden shadow-sm">
        <table className="w-full text-[11px]">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 text-[10px] uppercase tracking-wider">
              <th className="text-left px-3 py-2.5 font-semibold">Supplier</th>
              <th className="text-left px-3 py-2.5 font-semibold">Contact Person</th>
              <th className="text-left px-3 py-2.5 font-semibold">Phone</th>
              <th className="text-left px-3 py-2.5 font-semibold">NTN</th>
              <th className="text-left px-3 py-2.5 font-semibold">City</th>
              <th className="text-right px-3 py-2.5 font-semibold">Balance</th>
              <th className="text-center px-3 py-2.5 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((s, idx) => (
              <tr key={s.id} className={`border-b border-slate-100/80 hover:bg-blue-50/30 transition-colors duration-150 ${idx % 2 === 1 ? 'bg-slate-50/30' : ''}`}>
                <td className="px-3 py-2.5 font-semibold text-slate-800">{s.name}</td>
                <td className="px-3 py-2.5 text-slate-600">{s.contactPerson || '-'}</td>
                <td className="px-3 py-2.5 text-slate-600">{s.phone || '-'}</td>
                <td className="px-3 py-2.5 text-slate-500 font-mono">{s.ntn || '-'}</td>
                <td className="px-3 py-2.5 text-slate-600">{s.city || '-'}</td>
                <td className="px-3 py-2.5 text-right font-bold" style={{ color: (s.balance || 0) > 0 ? '#ef4444' : '#22c55e' }}>{f(s.balance || 0)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center justify-center gap-1.5">
                    <button onClick={() => openEdit(s)} className="text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all duration-200 p-1.5 rounded-lg" title="Edit">
                      <Edit3 className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(s.id)} className="text-slate-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 p-1.5 rounded-lg" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="text-center py-12">
              <Truck className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-400 text-xs">No suppliers found</p>
              <p className="text-slate-300 text-[10px] mt-0.5">Try adjusting your search</p>
            </td></tr>}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-3">
        <div className="bg-white rounded-xl border border-slate-200/60 p-3 text-center hover:shadow-md transition-all duration-200">
          <div className="text-[10px] text-slate-400 uppercase font-semibold">Total Suppliers</div>
          <div className="text-lg font-bold text-slate-800">{suppliers.length}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200/60 p-3 text-center hover:shadow-md transition-all duration-200">
          <div className="text-[10px] text-slate-400 uppercase font-semibold">Total Payable</div>
          <div className="text-lg font-bold text-red-500">Rs {f(totalPayable)}</div>
        </div>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
              <h3 className="text-sm font-bold text-slate-800">{editing ? 'Edit Supplier' : 'New Supplier'}</h3>
              <button onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-600 transition-colors"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-3">
              <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Company Name *</label><input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all" /></div>
              <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Contact Person</label><input value={form.contactPerson} onChange={e => setForm({ ...form, contactPerson: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Phone</label><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all" /></div>
                <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Email</label><input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all" /></div>
              </div>
              <div><label className="text-[10px] font-semibold text-slate-500 uppercase">NTN</label><input value={form.ntn} onChange={e => setForm({ ...form, ntn: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-[10px] font-semibold text-slate-500 uppercase">Address</label><input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all" /></div>
                <div><label className="text-[10px] font-semibold text-slate-500 uppercase">City</label><input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-slate-200 text-[12px] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/10 transition-all" /></div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl">
              <button onClick={() => setShowForm(false)} className="px-3 py-1.5 rounded-lg text-[11px] font-medium text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              <button onClick={handleSave} className="flex items-center gap-1 bg-blue-600 text-white px-4 py-1.5 rounded-lg text-[11px] font-semibold hover:bg-blue-700 transition-colors"><Save className="w-3 h-3" />Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}